import { baseApi } from "@/shared/api/baseApi";
import {
  merchantHomeApprovalResponseSchema, merchantHomeIntentResponseSchema, merchantHomeIntentStatusSchema,
  merchantHomeOpportunitiesSchema, merchantHomeOverviewSchema, merchantHomeReadResponseSchema,
  merchantHomeWorkDetailSchema, merchantHomeWorkPageSchema,
  type MerchantHomeActionPurpose, type MerchantHomeIntentType, type MerchantHomeWorkKind,
} from "./merchantHomeSchemas";

export interface MerchantHomeQuery {
  userId: string;
  organizationId: string;
  shopBindingId?: string;
  market?: string;
  category?: string;
}
export function merchantHomeIntentScopeKey(input: MerchantHomeQuery) {
  return JSON.stringify([input.userId, input.organizationId, input.shopBindingId ?? "", input.market ?? "", input.category ?? ""]);
}
export interface MerchantHomeIntentInput extends MerchantHomeQuery {
  actionPurpose?: MerchantHomeActionPurpose;
  intentType: MerchantHomeIntentType;
  idempotencyKey: string;
  reference?: { kind: "work_item" | "opportunity"; id: string; revision?: string };
}
export interface MerchantHomeApprovalInput extends MerchantHomeQuery {
  executionId: string;
  expectedPlanHash: string;
  expectedStateRevision: string;
  idempotencyKey: string;
  resourceIds: string[];
}
interface WorkQuery extends MerchantHomeQuery {
  kind: MerchantHomeWorkKind;
  snapshotId: string;
  cursor?: string;
  limit?: number;
}
interface DetailQuery extends MerchantHomeQuery { itemId: string; expectedResultRevision?: string }
interface ReadInput extends DetailQuery { resultRevision: string; idempotencyKey: string }
const path = (input: MerchantHomeQuery) => `/v1/organizations/${encodeURIComponent(input.organizationId)}/merchant-home`;
const params = (input: MerchantHomeQuery) => ({
  ...(input.shopBindingId ? { shop_binding_id: input.shopBindingId } : {}),
  ...(input.market ? { market: input.market } : {}),
  ...(input.category ? { category: input.category } : {}),
});
const tag = (input: MerchantHomeQuery) => ({ type: "MerchantHome" as const, id: `${input.userId}:${input.organizationId}:${input.shopBindingId ?? ""}` });
export function merchantHomeIntentRequest(input: MerchantHomeIntentInput) {
  return {
    url: `${path(input)}/intents`,
    method: "POST" as const,
    body: {
      intent_type: input.intentType,
      ...(input.actionPurpose ? { action_purpose: input.actionPurpose } : {}),
      shop_binding_id: input.shopBindingId ?? "",
      market: input.market ?? "",
      category: input.category ?? "",
      reference: input.reference,
      idempotency_key: input.idempotencyKey,
    },
  };
}
export const merchantHomeApi = baseApi.enhanceEndpoints({ addTagTypes: ["MerchantHome"] }).injectEndpoints({
  endpoints: (builder) => ({
    getMerchantHome: builder.query<ReturnType<typeof merchantHomeOverviewSchema.parse>, MerchantHomeQuery>({
      query: (input) => ({ url: path(input), params: params(input) }),
      transformResponse: (response) => merchantHomeOverviewSchema.parse(response),
      providesTags: (_result, _error, input) => [tag(input), "MerchantStoreBindings", "ExperienceStrategyCurrent"],
    }),
    getMerchantHomeWorkItems: builder.query<ReturnType<typeof merchantHomeWorkPageSchema.parse>, WorkQuery>({
      query: (input) => ({ url: `${path(input)}/work-items`, params: { ...params(input), kind: input.kind, snapshot_id: input.snapshotId, cursor: input.cursor ?? "", limit: input.limit ?? 10 } }),
      transformResponse: (response) => merchantHomeWorkPageSchema.parse(response),
      providesTags: (_result, _error, input) => [tag(input)],
    }),
    getMerchantHomeWorkItem: builder.query<ReturnType<typeof merchantHomeWorkDetailSchema.parse>, DetailQuery>({
      query: (input) => ({ url: `${path(input)}/work-items/${encodeURIComponent(input.itemId)}`, params: { expected_result_revision: input.expectedResultRevision } }),
      transformResponse: (response) => merchantHomeWorkDetailSchema.parse(response),
      providesTags: (_result, _error, input) => [tag(input)],
    }),
    readMerchantHomeWorkItem: builder.mutation<ReturnType<typeof merchantHomeReadResponseSchema.parse>, ReadInput>({
      query: (input) => ({ url: `${path(input)}/work-items/${encodeURIComponent(input.itemId)}/read`, method: "POST", body: { result_revision: input.resultRevision, idempotency_key: input.idempotencyKey } }),
      transformResponse: (response) => merchantHomeReadResponseSchema.parse(response),
      invalidatesTags: (_result, _error, input) => [tag(input)],
    }),
    getMerchantHomeOpportunities: builder.query<ReturnType<typeof merchantHomeOpportunitiesSchema.parse>, MerchantHomeQuery & { cardType?: string; cursor?: string; limit?: number }>({
      query: (input) => ({ url: `${path(input)}/opportunities`, params: { ...params(input), card_type: input.cardType, cursor: input.cursor, limit: input.limit ?? 50 } }),
      transformResponse: (response) => merchantHomeOpportunitiesSchema.parse(response),
      providesTags: (_result, _error, input) => [tag(input), "MerchantStoreBindings"],
    }),
    getMerchantHomeIntent: builder.query<ReturnType<typeof merchantHomeIntentStatusSchema.parse>, MerchantHomeQuery & { operationId: string }>({
      query: input => `${path(input)}/intents/${encodeURIComponent(input.operationId)}`,
      transformResponse: response => merchantHomeIntentStatusSchema.parse(response),
      providesTags: (_result, _error, input) => [{ type: "MerchantHome", id: input.operationId }],
    }),
    resumeMerchantHomeIntent: builder.mutation<ReturnType<typeof merchantHomeIntentStatusSchema.parse>, MerchantHomeQuery & { operationId: string }>({
      query: input => ({ url: `${path(input)}/intents/${encodeURIComponent(input.operationId)}/resume`, method: "POST", body: {} }),
      transformResponse: response => merchantHomeIntentStatusSchema.parse(response),
      invalidatesTags: (_result, _error, input) => [{ type: "MerchantHome", id: input.operationId }, "Conversations"],
    }),
    createMerchantHomeIntent: builder.mutation<ReturnType<typeof merchantHomeIntentResponseSchema.parse>, MerchantHomeIntentInput>({
      query: merchantHomeIntentRequest,
      transformResponse: (response) => merchantHomeIntentResponseSchema.parse(response),
      invalidatesTags: ["Conversations"],
    }),
    getMerchantHomeApproval: builder.query<ReturnType<typeof merchantHomeApprovalResponseSchema.parse>, MerchantHomeQuery & { executionId: string; expectedStateRevision: string }>({
      query: input => ({ url: `${path(input)}/approvals/${encodeURIComponent(input.executionId)}`, params: { expected_state_revision: input.expectedStateRevision } }),
      transformResponse: response => merchantHomeApprovalResponseSchema.parse(response),
      providesTags: (_result, _error, input) => [tag(input)],
    }),
    approveMerchantHomeExecution: builder.mutation<ReturnType<typeof merchantHomeApprovalResponseSchema.parse>, MerchantHomeApprovalInput>({
      query: (input) => ({ url: `${path(input)}/approvals/${encodeURIComponent(input.executionId)}/approve`, method: "POST", body: { shop_binding_id: input.shopBindingId ?? "", expected_plan_hash: input.expectedPlanHash, expected_state_revision: input.expectedStateRevision, idempotency_key: input.idempotencyKey, resource_ids: input.resourceIds } }),
      transformResponse: (response) => merchantHomeApprovalResponseSchema.parse(response),
      invalidatesTags: (_result, _error, input) => [tag(input)],
    }),
  }),
});
export const {
  useGetMerchantHomeQuery, useGetMerchantHomeWorkItemsQuery, useGetMerchantHomeWorkItemQuery,
  useLazyGetMerchantHomeWorkItemQuery, useReadMerchantHomeWorkItemMutation,
  useGetMerchantHomeOpportunitiesQuery, useCreateMerchantHomeIntentMutation,
  useApproveMerchantHomeExecutionMutation, useGetMerchantHomeIntentQuery, useResumeMerchantHomeIntentMutation,
  useGetMerchantHomeApprovalQuery, useLazyGetMerchantHomeApprovalQuery,
} = merchantHomeApi;
